import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';

const SERVICES = [
  {
    id: 'gsc',
    name: 'Google Search Console',
    description: 'Monitor search performance, indexing, and keywords — required to capture your baseline',
    icon: '🔍',
    helpUrl: 'https://support.google.com/webmasters/answer/9008080',
    required: true,
  },
  {
    id: 'ga4',
    name: 'Google Analytics 4',
    description: 'Track website traffic, user behavior, and conversions',
    icon: '📊',
    helpUrl: 'https://support.google.com/analytics/answer/9304153',
    recommended: true,
  },
  {
    id: 'gbp',
    name: 'Google Business Profile',
    description: 'Manage local presence, reviews, and business info',
    icon: '📍',
    helpUrl: 'https://support.google.com/business/answer/2911778',
  },
];

function GA4PropertySelector({ onRefresh }) {
  const [properties, setProperties] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/analytics/properties');
      setProperties(res.data.properties || []);
      setSelectedId(res.data.selected_property_id || null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load properties';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (propertyId) => {
    setSaving(true);
    try {
      await api.post('/analytics/select-property', { property_id: propertyId });
      setSelectedId(propertyId);
      toast.success('GA4 property selected');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to select property');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3 pl-16">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading properties...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500 py-3 pl-16">
        <XCircle className="w-4 h-4" /> {error}
        <button onClick={fetchProperties} className="underline ml-2">Retry</button>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3 pl-16">
        No GA4 properties found in this Google account.
      </div>
    );
  }

  return (
    <div className="mt-3 ml-16">
      {properties.length === 1 ? (() => {
        const prop = properties[0];
        const isSelected = selectedId === prop.property_id;
        return (
          <div className={`flex items-center justify-between p-3 rounded-lg text-sm ${
            isSelected 
              ? 'bg-indigo-50 border border-indigo-200' 
              : 'bg-slate-50 border border-slate-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Property:</span>
              <span className="font-medium">{prop.display_name}</span>
              <span className="text-xs text-slate-400">({prop.property_id})</span>
            </div>
            {isSelected ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Active
              </span>
            ) : (
              <button
                onClick={() => handleSelect(prop.property_id)}
                disabled={saving}
                className="text-xs text-indigo-600 font-medium hover:underline"
              >
                {saving ? 'Selecting...' : 'Select'}
              </button>
            )}
          </div>
        );
      })() : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">Select a GA4 Property:</p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {properties.map((prop) => (
              <button
                key={prop.property_id}
                onClick={() => handleSelect(prop.property_id)}
                disabled={saving}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${
                  selectedId === prop.property_id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{prop.display_name}</p>
                    <p className="text-xs text-slate-500">{prop.account_name} &middot; {prop.property_id}</p>
                  </div>
                  {selectedId === prop.property_id && (
                    <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GscSiteSelector({ onRefresh }) {
  const [sites, setSites] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/analytics/gsc/sites');
      setSites(res.data.sites || []);
      setSelectedUrl(res.data.selected_site_url || null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load sites';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (siteUrl) => {
    setSaving(true);
    try {
      await api.post('/analytics/gsc/select-site', { site_url: siteUrl });
      setSelectedUrl(siteUrl);
      toast.success('GSC site selected');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to select site');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3 pl-16">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading sites...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500 py-3 pl-16">
        <XCircle className="w-4 h-4" /> {error}
        <button onClick={fetchSites} className="underline ml-2">Retry</button>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3 pl-16">
        No verified sites found in this Google account.
      </div>
    );
  }

  return (
    <div className="mt-3 ml-16">
      {sites.length === 1 ? (() => {
        const site = sites[0];
        const isSelected = selectedUrl === site.site_url;
        return (
          <div className={`flex items-center justify-between p-3 rounded-lg text-sm ${
            isSelected
              ? 'bg-indigo-50 border border-indigo-200'
              : 'bg-slate-50 border border-slate-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Site:</span>
              <span className="font-medium">{site.site_url}</span>
            </div>
            {isSelected ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Active
              </span>
            ) : (
              <button
                onClick={() => handleSelect(site.site_url)}
                disabled={saving}
                className="text-xs text-indigo-600 font-medium hover:underline"
              >
                {saving ? 'Selecting...' : 'Select'}
              </button>
            )}
          </div>
        );
      })() : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">Select a Search Console Site:</p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {sites.map((site) => (
              <button
                key={site.site_url}
                onClick={() => handleSelect(site.site_url)}
                disabled={saving}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${
                  selectedUrl === site.site_url
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{site.site_url}</p>
                    <p className="text-xs text-slate-500">{site.permission_level}</p>
                  </div>
                  {selectedUrl === site.site_url && (
                    <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GBPLocationSelector({ onRefresh }) {
  const [locations, setLocations] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/gbp/list-locations');
      setLocations(res.data.locations || []);
      setSelectedName(res.data.selected_location_name || null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load locations';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (locationName, accountName) => {
    setSaving(true);
    try {
      await api.post('/gbp/select-location', { location_name: locationName, account_name: accountName });
      setSelectedName(locationName);
      toast.success('GBP location selected');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to select location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3 pl-16">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading businesses...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500 py-3 pl-16">
        <XCircle className="w-4 h-4" /> {error}
        <button onClick={fetchLocations} className="underline ml-2">Retry</button>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3 pl-16">
        No GBP locations found in this Google account.
      </div>
    );
  }

  return (
    <div className="mt-3 ml-16">
      {locations.length === 1 ? (() => {
        const loc = locations[0];
        const isSelected = selectedName === loc.location_name;
        return (
          <div className={`flex items-center justify-between p-3 rounded-lg text-sm ${
            isSelected
              ? 'bg-indigo-50 border border-indigo-200'
              : 'bg-slate-50 border border-slate-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Business:</span>
              <span className="font-medium">{loc.title}</span>
              {loc.address && <span className="text-xs text-slate-400">{loc.address}</span>}
            </div>
            {isSelected ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Active
              </span>
            ) : (
              <button
                onClick={() => handleSelect(loc.location_name, loc.account_name)}
                disabled={saving}
                className="text-xs text-indigo-600 font-medium hover:underline"
              >
                {saving ? 'Selecting...' : 'Select'}
              </button>
            )}
          </div>
        );
      })() : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">Select a Business Location:</p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {locations.map((loc) => (
              <button
                key={loc.location_name}
                onClick={() => handleSelect(loc.location_name, loc.account_name)}
                disabled={saving}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${
                  selectedName === loc.location_name
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{loc.title}</p>
                    <p className="text-xs text-slate-500">
                      {loc.account_display}
                      {loc.address ? ` · ${loc.address}` : ''}
                    </p>
                  </div>
                  {selectedName === loc.location_name && (
                    <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsSetupStep({ data, onNext, onBack, onRefresh }) {
  const [connecting, setConnecting] = useState(null);

  const getStatusBadge = (service) => {
    const status = data?.[service]?.status || 'not_connected';
    
    switch (status) {
      case 'connected':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pending</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Not Connected</Badge>;
    }
  };

  const isConnected = (service) => data?.[service]?.status === 'connected';

  const handleConnect = async (serviceId) => {
    setConnecting(serviceId);
    
    try {
      // Use direct Google OAuth to get real API tokens
      const redirectUri = window.location.origin + '/oauth/google/callback';
      const res = await api.get('/integrations/google/direct-auth-url', {
        params: { service: serviceId, redirect_uri: redirectUri }
      });
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate connection');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (serviceId) => {
    try {
      await api.post('/integrations/google/disconnect', { service: serviceId });
      toast.success(`${serviceId.toUpperCase()} disconnected`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const gscConnected = isConnected('gsc');
  const ga4Connected = isConnected('ga4');

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold font-heading mb-2">Connect Your Analytics</h1>
        <p className="text-slate-600 max-w-lg mx-auto">
          Connect your Google accounts to enable data tracking, performance monitoring, and automated reporting.
        </p>
      </div>

      <div className="space-y-4">
        {SERVICES.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className={`gds-card transition-all ${isConnected(service.id) ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-2xl">
                      {service.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{service.name}</h3>
                        {getStatusBadge(service.id)}
                        {service.required && !isConnected(service.id) && (
                          <Badge className="bg-red-100 text-red-700">Required</Badge>
                        )}
                        {service.recommended && !isConnected(service.id) && (
                          <Badge className="bg-amber-100 text-amber-700">Recommended</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                      {isConnected(service.id) && data?.[service.id]?.google_email && (
                        <p className="text-xs text-slate-500 mt-1">
                          Connected as: {data[service.id].google_email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(service.helpUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    
                    {isConnected(service.id) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisconnect(service.id)}
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="gds-btn-primary"
                        onClick={() => handleConnect(service.id)}
                        disabled={connecting === service.id}
                      >
                        {connecting === service.id ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                        ) : (
                          'Connect'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
              {service.id === 'gsc' && isConnected('gsc') && (
                <GscSiteSelector onRefresh={onRefresh} />
              )}
              {service.id === 'ga4' && isConnected('ga4') && (
                <GA4PropertySelector onRefresh={onRefresh} />
              )}
              {service.id === 'gbp' && isConnected('gbp') && (
                <GBPLocationSelector onRefresh={onRefresh} />
              )}
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Help Card */}
      <Card className="gds-card bg-blue-50/50 border-blue-200">
        <CardContent className="p-6">
          <h4 className="font-medium mb-2">Don't have these accounts set up?</h4>
          <p className="text-sm text-slate-600 mb-4">
            Google Search Console is free and takes a few minutes to set up — it's needed to capture your keyword and traffic baseline. GA4 and GBP can be connected later from your dashboard settings.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open('https://search.google.com/search-console', '_blank')}>
              Set up GSC (Free)
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open('https://analytics.google.com', '_blank')}>
              Create GA4 Account
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open('https://business.google.com', '_blank')}>
              Create GBP Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* GA4 nudge when GSC is connected but GA4 isn't */}
      {gscConnected && !ga4Connected && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          GA4 is not connected — organic traffic won't appear in your baseline. You can connect it now or later from Integrations.
        </p>
      )}

      {/* GSC required blocker */}
      {!gscConnected && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          Connect Google Search Console to continue — it's required to capture your keyword and ranking baseline.
        </p>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={onNext}
          className="gds-btn-primary"
          disabled={!gscConnected}
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
